// ============================================
// RenderPass - High-level render pass abstraction
// ============================================

import { type IRenderContext } from './IRenderContext';
import {
  type RenderTargetHandle,
  type TextureHandle,
  NULL_HANDLE,
} from '../types';

export interface RenderPassOptions {
  /** Clear color (RGBA, 0-1) */
  clearColor?: [number, number, number, number];
  /** Render target (null for screen) */
  target?: RenderTargetHandle | null;
  /** Viewport (defaults to full target) */
  viewport?: { x: number; y: number; width: number; height: number };
}

/**
 * RenderPass encapsulates a rendering pass with clear/target setup
 */
export class RenderPass {
  private ctx: IRenderContext;
  private target: RenderTargetHandle | null;
  private clearColor: [number, number, number, number] | null;
  private viewport: { x: number; y: number; width: number; height: number } | null;
  private active = false;
  
  constructor(ctx: IRenderContext, options: RenderPassOptions = {}) {
    this.ctx = ctx;
    this.target = options.target ?? null;
    this.clearColor = options.clearColor ?? null;
    this.viewport = options.viewport ?? null;
  }
  
  /**
   * Begin this render pass
   */
  begin(): void {
    if (this.active) {
      throw new Error('RenderPass already active');
    }
    
    this.active = true;
    
    // Set render target
    this.ctx.setRenderTarget(this.target);
    
    // Set viewport
    if (this.viewport) {
      this.ctx.setViewport(
        this.viewport.x,
        this.viewport.y,
        this.viewport.width,
        this.viewport.height
      );
    }
    
    // Clear
    if (this.clearColor) {
      this.ctx.clear(
        this.clearColor[0],
        this.clearColor[1],
        this.clearColor[2],
        this.clearColor[3]
      );
    }
  }
  
  /**
   * End this render pass
   */
  end(): void {
    if (!this.active) {
      throw new Error('RenderPass not active');
    }
    
    this.active = false;
    
    // Reset to default target if we were using offscreen
    if (this.target !== null) {
      this.ctx.setRenderTarget(null);
    }
  }
}

/**
 * OffscreenTarget - Offscreen render target wrapper
 */
export class OffscreenTarget {
  private ctx: IRenderContext;
  private _handle: RenderTargetHandle;
  private _width: number;
  private _height: number;
  private _hasDepth: boolean;
  private _hdr: boolean;
  
  constructor(ctx: IRenderContext, width: number, height: number, hasDepth = false, hdr = false) {
    this.ctx = ctx;
    this._width = width;
    this._height = height;
    this._hasDepth = hasDepth;
    this._hdr = hdr;
    this._handle = ctx.createRenderTarget(width, height, hasDepth, hdr);
  }
  
  /**
   * Get render target handle
   */
  get handle(): RenderTargetHandle {
    return this._handle;
  }
  
  /**
   * Get width
   */
  get width(): number {
    return this._width;
  }
  
  /**
   * Get height
   */
  get height(): number {
    return this._height;
  }
  
  /**
   * Get the color texture for sampling
   */
  getTexture(): TextureHandle {
    return this.ctx.getRenderTargetTexture(this._handle);
  }
  
  /**
   * Resize the render target
   */
  resize(width: number, height: number): void {
    if (width === this._width && height === this._height) return;
    
    // Destroy old and create new
    this.ctx.destroyRenderTarget(this._handle);
    this._width = width;
    this._height = height;
    this._handle = this.ctx.createRenderTarget(width, height, this._hasDepth, this._hdr);
  }
  
  /**
   * Create a render pass for this target
   */
  createPass(clearColor?: [number, number, number, number]): RenderPass {
    return new RenderPass(this.ctx, {
      target: this._handle,
      clearColor,
    });
  }
  
  /**
   * Destroy this render target
   */
  destroy(): void {
    this.ctx.destroyRenderTarget(this._handle);
  }
}
